import { Suspense } from 'react';

import { PremiumFeatureRoute, TryPremiumFeature } from '@cast/shared-trial-ui';
import { getDocsLink } from '@cast/utils';

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import { DrawerProvider } from '@cast/design-system';
import {
  ClusterProvider,
  useClusterQuery,
} from '@cast/shared-cluster-context-data-access';
import { isDisconnectedForMonth } from '@cast/features-cluster-shared-utils';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { SuspenseLoader } from 'components/loaders';
import { ProtectedRoute } from 'components/router';
import { PremiumAutoscalerRoute } from 'components/trial/PremiumAutoscalerRoute';

import { buildClusterInventoryBasePath } from '@oneclick/constants/navigation';
import { renderConfigRoutes } from '@oneclick/routing';

import {
  getClusterMountedPages,
  type ClusterLinkItem,
} from './clusterRouteConfig';
import { ClusterDashboardPage } from './ClusterDashboardPage';
import { InaccessibleClusterDashboardPage } from './InaccessibleClusterDashboardPage';

const LegacyNodesRedirect = () => {
  const { clusterId, '*': rest } = useParams<{
    clusterId: string;
    '*': string;
  }>();
  const { search, hash } = useLocation();
  const suffix = rest ? `/${rest}` : '';

  return (
    <Navigate
      to={`${buildClusterInventoryBasePath(
        clusterId!
      )}/nodes${suffix}${search}${hash}`}
      replace
    />
  );
};

const CostReportToInventoryRedirect = ({
  inventorySegment,
}: {
  inventorySegment: string;
}) => {
  const { clusterId, '*': rest } = useParams<{
    clusterId: string;
    '*': string;
  }>();
  const { search, hash } = useLocation();
  const suffix = rest ? `/${rest}` : '';

  return (
    <Navigate
      to={`${buildClusterInventoryBasePath(
        clusterId!
      )}/${inventorySegment}${suffix}${search}${hash}`}
      replace
    />
  );
};

const pagesByWrapper = (
  pages: ClusterLinkItem[],
  wrapper: ClusterLinkItem['wrapper'] | undefined
) => pages.filter((p) => p.wrapper === wrapper);

export const ClusterController = () => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { isLoading, error, cluster, refetch } = useClusterQuery(clusterId);

  useSetAnalyticsGlobal('clusterId', cluster?.id);
  useSetAnalyticsGlobal('clusterName', cluster?.name);

  if (error && !cluster) {
    return (
      <FailedToLoad
        title="Failed to load cluster"
        refresh={refetch}
        testId="cluster-error"
      />
    );
  }

  if (isLoading || !cluster) {
    return <SuspenseLoader />;
  }

  const isInaccessible = isDisconnectedForMonth(cluster);
  const mounted = getClusterMountedPages();
  const unwrapped = pagesByWrapper(mounted, undefined);
  const premiumAutoscaler = pagesByWrapper(mounted, 'premiumAutoscaler');
  const premiumWorkload = pagesByWrapper(mounted, 'premiumWorkload');
  const vulnerabilities = pagesByWrapper(mounted, 'vulnerabilities');
  const auditLogs = pagesByWrapper(mounted, 'auditLogs');

  const Dashboard = isInaccessible
    ? InaccessibleClusterDashboardPage
    : ClusterDashboardPage;

  return (
    <ClusterProvider cluster={cluster} isLoading={isLoading}>
      <DrawerProvider>
        <Suspense fallback={<SuspenseLoader />}>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route
              path="cost-report/namespaces/*"
              element={
                <CostReportToInventoryRedirect inventorySegment="namespaces" />
              }
            />
            <Route
              path="cost-report/allocation-groups/*"
              element={
                <CostReportToInventoryRedirect inventorySegment="allocation-groups" />
              }
            />
            {renderConfigRoutes({ pages: unwrapped, suspense: false })}
            <Route element={<PremiumAutoscalerRoute />}>
              {renderConfigRoutes({
                pages: premiumAutoscaler,
                suspense: false,
              })}
            </Route>
            <Route
              element={
                <PremiumFeatureRoute
                  paywall={
                    <TryPremiumFeature
                      name="Workload Optimization"
                      docs={{
                        link: getDocsLink('docs_workload_autoscaler'),
                      }}
                    />
                  }
                />
              }
            >
              {renderConfigRoutes({
                pages: premiumWorkload,
                suspense: false,
              })}
            </Route>
            <Route
              element={
                <ProtectedRoute subject="OrganizationSecurityVulnerabilities" />
              }
            >
              {renderConfigRoutes({
                pages: vulnerabilities,
                suspense: false,
              })}
            </Route>
            <Route element={<ProtectedRoute subject="ClusterAuditLog" />}>
              <Route
                element={
                  <PremiumFeatureRoute
                    paywall={
                      <TryPremiumFeature
                        name="Logs"
                        docs={{
                          link: getDocsLink('docs_audit'),
                        }}
                      />
                    }
                  />
                }
              >
                {renderConfigRoutes({
                  pages: auditLogs,
                  suspense: false,
                })}
              </Route>
            </Route>
            <Route path="nodes/*" element={<LegacyNodesRedirect />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </Suspense>
      </DrawerProvider>
    </ClusterProvider>
  );
};
